import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Row, Col } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faCheckCircle, faTimesCircle, faChevronLeft,
    faSpinner, faInfoCircle, faExclamationTriangle,
    faClock, faFileContract, faBan, faEdit, faSave
} from '@fortawesome/free-solid-svg-icons'
import { fetchPurchaseRequestById, updatePurchaseRequest, updateSalesOrder } from './purchaseRequestService'
import { API_URL } from '../utils/Constants/URLS'
import PageHeader from '../utils/PageHeader'
import '../../css/purchase.css'
import '../../css/common.css'
import '../../css/SalesOrder.css'

/* ─── Role helper ────────────────────────────────────────── */
const CAN_APPROVE_ROLES = ['commercial_manager']
const canApprove = (role) => CAN_APPROVE_ROLES.includes(role?.toLowerCase())

/* ─── Helpers ─────────────────────────────────────────────── */
const formatDate = (d) =>
    d
        ? new Date(d).toLocaleDateString('en-GB', {
            day: '2-digit', month: 'short', year: 'numeric',
        })
        : '—'

/* ─── Status badge ────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
    const map = {
        pending: {
            wrapper: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-orange-50 border border-orange-200 text-orange-700',
            icon: faClock,
            label: 'Pending Review',
        },
        approved: {
            wrapper: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-green-50 border border-green-200 text-green-700',
            icon: faCheckCircle,
            label: 'Approved',
        },
        rejected: {
            wrapper: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold bg-red-50 border border-red-200 text-red-700',
            icon: faTimesCircle,
            label: 'Rejected',
        },
    }
    const cfg = map[status?.toLowerCase()] ?? map.pending
    return (
        <span className={cfg.wrapper}>
            <FontAwesomeIcon icon={cfg.icon} />
            {cfg.label}
        </span>
    )
}

const OemBadge = ({ isOem }) => (
    <span
        className={
            isOem
                ? 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-purple-50 border border-purple-200 text-purple-700'
                : 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-gray-50 border border-gray-200 text-gray-600'
        }
    >
        {isOem ? 'OEM' : 'Non-OEM'}
    </span>
)

/* ─── Info field ──────────────────────────────────────────── */
const InfoField = ({ label, value, colMd = 3 }) => (
    <Col md={colMd} className='mb-3'>
        <div className='text-xs text-gray-400 font-bold mb-0.5 uppercase tracking-wide'>
            {label}
        </div>
        <div className='text-sm font-bold text-gray-800'>{value || '—'}</div>
    </Col>
)

/* ─── Reject modal ────────────────────────────────────────── */
const RejectModal = ({ onConfirm, onCancel, loading }) => {
    const [reason, setReason] = useState('')
    const [err, setErr] = useState('')

    const handleConfirm = () => {
        if (!reason.trim()) { setErr('Rejection reason is required.'); return }
        setErr('')
        onConfirm(reason.trim())
    }

    return (
        <div className='fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center p-4'>
            <div className='bg-white rounded-2xl p-7 w-full max-w-md shadow-2xl'>
                {/* Header */}
                <div className='flex items-center gap-3 mb-5'>
                    <div className='w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0'>
                        <FontAwesomeIcon icon={faBan} className='text-red-700 text-lg' />
                    </div>
                    <div>
                        <h5 className='m-0 font-extrabold text-gray-800'>
                            Reject Purchase Request
                        </h5>
                        <p className='m-0 text-xs text-gray-500'>
                            Provide a reason before rejecting.
                        </p>
                    </div>
                </div>

                <label className='form-label text-sm font-bold text-gray-700 mb-1.5 block'>
                    Rejection Reason <span className='text-red-500'>*</span>
                </label>
                <textarea
                    id='reject-reason-input'
                    className='form-control'
                    rows={4}
                    value={reason}
                    onChange={(e) => { setReason(e.target.value); setErr('') }}
                    placeholder='Explain why this purchase request is being rejected…'
                />
                {err && (
                    <div className='flex items-center gap-1.5 text-red-600 text-xs mt-1.5'>
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {err}
                    </div>
                )}

                <div className='flex justify-end gap-2.5 mt-5'>
                    <button
                        id='btn-cancel-reject'
                        className='btn btn-light border font-semibold'
                        onClick={onCancel}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button
                        id='btn-confirm-reject'
                        className='btn btn-danger px-4 font-bold flex items-center gap-2'
                        onClick={handleConfirm}
                        disabled={loading}
                    >
                        {loading
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Rejecting…</>
                            : <><FontAwesomeIcon icon={faTimesCircle} /> Confirm Rejection</>}
                    </button>
                </div>
            </div>
        </div>
    )
}

/* ══════════════════════════════════════════
   PRDetail — Purchase Request Detail Page
══════════════════════════════════════════ */
const PRDetail = () => {
    const { prid } = useParams()
    const navigate = useNavigate()
    const token = useSelector((store) => store.user?.token) || localStorage.getItem('token')
    const userDetails = useSelector((store) => store.user)
    const userRole = userDetails?.role?.toLowerCase()

    const [pr, setPR] = useState(null)
    const [loading, setLoading] = useState(true)
    const [fetchError, setFetchErr] = useState('')
    const [approvalStatus, setStatus] = useState('')

    const [actionLoading, setActionLoading] = useState(false)
    const [actionError, setActionError] = useState('')
    const [actionSuccess, setActionSuccess] = useState('')
    const [showReject, setShowReject] = useState(false)

    const [isEditing, setIsEditing] = useState(false)
    const [editLineItems, setEditLineItems] = useState([])
    const [soDataForEdit, setSoDataForEdit] = useState(null)

    /* ── Fetch ── */
    useEffect(() => {
        if (!prid) return
        const load = async () => {
            setLoading(true)
            setFetchErr('')
            try {
                const res = await fetchPurchaseRequestById(token, prid)
                const data = res?.data ?? res
                setPR(data)
                setStatus(data?.approvalStatus ?? '')
            } catch (e) {
                console.error(e)
                setFetchErr(e.message || 'Failed to load PR details.')
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [prid, token])

    /* ── Approve ── */
    const handleApprove = async () => {
        setActionError('')
        setActionSuccess('')
        setActionLoading(true)
        try {
            await updatePurchaseRequest(token, prid, { approvalStatus: 'approved' })
            setStatus('approved')
            setActionSuccess('Purchase Request approved successfully.')
            setPR((p) => p ? { ...p, approvalStatus: 'approved' } : p)
        } catch (e) {
            setActionError(e.message || 'Failed to approve.')
        } finally {
            setActionLoading(false)
        }
    }

    /* ── Reject ── */
    const handleReject = async (reason) => {
        setActionError('')
        setActionSuccess('')
        setActionLoading(true)
        try {
            await updatePurchaseRequest(token, prid, {
                approvalStatus: 'rejected',
                rejectionReason: reason,
            })

            // If linked to SO, revert purchasedQuantity
            const soId = pr.salesOrder?._id || pr.salesOrder
            if (soId && typeof soId === 'string' && soId !== '—') {
                const soRes = await fetch(`${API_URL}leads/sales-orders/${soId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
                const soResult = await soRes.json()
                const soData = soResult?.data || {}

                if (soData && Array.isArray(soData.lineItems)) {
                    const updatedSoLineItems = soData.lineItems.map(orig => {
                        const origModelId = String(orig?.model?._id || orig?.model || '')
                        const matched = (pr.lineItems || []).find((p) => String(p.model?._id || p.model || '') === origModelId)
                        if (!matched) return orig

                        const currentPQty = Number(orig.purchasedQuantity || 0)
                        const prQty = Number(matched.quantity || 0)

                        // Revert the purchasedQuantity (minimum 0)
                        const newPQty = Math.max(0, currentPQty - prQty)

                        return { ...orig, purchasedQuantity: newPQty }
                    })

                    await updateSalesOrder(token, soId, {
                        lineItems: updatedSoLineItems,
                        status: soData.status ?? 'pending'
                    })
                }
            }

            setStatus('rejected')
            setShowReject(false)
            setActionSuccess('Purchase Request rejected. SO purchased quantity reverted.')
            setPR((p) => p ? { ...p, approvalStatus: 'rejected', rejectionReason: reason } : p)
        } catch (e) {
            console.error(e)
            setActionError(e.message || 'Failed to reject.')
        } finally {
            setActionLoading(false)
        }
    }

    /* ── Edit & Resubmit ── */
    const handleEditClick = async () => {
        setActionLoading(true)
        setActionError('')
        try {
            const soId = pr.salesOrder?._id || pr.salesOrder
            if (!soId || soId === '—') throw new Error('No linked Sales Order found.')

            const soRes = await fetch(`${API_URL}leads/sales-orders/${soId}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
            const soResult = await soRes.json()
            const soData = soResult?.data || {}
            setSoDataForEdit(soData)

            const initialEdits = (pr.lineItems || []).map(orig => {
                const modelId = String(orig.model?._id || orig.model || '')
                const soItem = (soData.lineItems || []).find(soi => String(soi.model?._id || soi.model || '') === modelId)

                const maxQty = soItem ? Math.max(0, Number(soItem.quantity || 0) - Number(soItem.purchasedQuantity || 0)) : 0

                return {
                    ...orig,
                    maxQty,
                    editQty: Number(orig.quantity || 0),
                    editSpec: orig.specification || ''
                }
            })
            setEditLineItems(initialEdits)
            setIsEditing(true)
        } catch (e) {
            setActionError(e.message || 'Failed to fetch Sales Order details for editing.')
        } finally {
            setActionLoading(false)
        }
    }

    const handleQtyChange = (idx, val, max) => {
        const edits = [...editLineItems]
        edits[idx].editQty = Math.min(Math.max(0, Number(val || 0)), max)
        setEditLineItems(edits)
    }

    const handleSpecChange = (idx, val) => {
        const edits = [...editLineItems]
        edits[idx].editSpec = val
        setEditLineItems(edits)
    }

    const handleResubmit = async () => {
        setActionLoading(true)
        setActionError('')
        setActionSuccess('')
        try {
            const selected = editLineItems.filter(li => li.editQty > 0).map(li => ({
                model: li.model?._id || li.model,
                quantity: li.editQty,
                specification: li.editSpec,
                requiredDeliveryDate: li.requiredDeliveryDate,
                lastDateOfOrderDelivery: li.lastDateOfOrderDelivery
            }))

            if (selected.length === 0) {
                throw new Error('Please request a quantity > 0 for at least one item.')
            }

            const soId = pr.salesOrder?._id || pr.salesOrder
            if (soId && soDataForEdit) {
                const updatedSoLineItems = (soDataForEdit.lineItems || []).map(orig => {
                    const origModelId = String(orig?.model?._id || orig?.model || '')
                    const matched = selected.find(m => String(m.model) === origModelId)
                    if (!matched) return orig

                    const currentPQty = Number(orig.purchasedQuantity || 0)
                    const addedQty = Number(matched.quantity || 0)
                    const newPQty = Math.min(currentPQty + addedQty, Number(orig.quantity || 0))

                    return { ...orig, purchasedQuantity: newPQty }
                })

                await updateSalesOrder(token, soId, {
                    lineItems: updatedSoLineItems,
                    status: soDataForEdit.status ?? 'pending'
                })
            }

            await updatePurchaseRequest(token, prid, {
                approvalStatus: 'pending',
                lineItems: selected,
            })

            setStatus('pending')
            setIsEditing(false)
            setActionSuccess('Purchase Request resubmitted successfully.')
            const res = await fetchPurchaseRequestById(token, prid)
            setPR(res?.data ?? res)
        } catch (e) {
            setActionError(e.message || 'Failed to resubmit PR.')
        } finally {
            setActionLoading(false)
        }
    }

    /* ── Loading ── */
    if (loading) {
        return (
            <div>
                <PageHeader pageName='PR DETAILS' breadcrump1='View PR' breadcrump2='Loading…' link='/view_pr' />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3 text-center py-20 text-gray-400'>
                    <FontAwesomeIcon icon={faSpinner} spin className='text-4xl mb-3' />
                    <p className='text-sm'>Loading purchase request…</p>
                </div>
            </div>
        )
    }

    /* ── Fetch error ── */
    if (fetchError) {
        return (
            <div>
                <PageHeader pageName='PR DETAILS' breadcrump1='View PR' breadcrump2='Error' link='/view_pr' />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3 text-center py-20'>
                    <FontAwesomeIcon icon={faExclamationTriangle} className='text-4xl mb-3 text-red-400' />
                    <p className='font-semibold text-red-500'>{fetchError}</p>
                    <button className='btn btn-light border mt-3' onClick={() => navigate('/view_pr')}>
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    /* ── Not found ── */
    if (!pr) {
        return (
            <div>
                <PageHeader pageName='PR DETAILS' breadcrump1='View PR' breadcrump2='Not Found' link='/view_pr' />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3 text-center py-20 text-gray-400'>
                    <FontAwesomeIcon icon={faInfoCircle} className='text-4xl mb-3 text-gray-300' />
                    <p className='font-semibold text-gray-500'>Purchase Request Not Found</p>
                    <button className='btn btn-light border mt-3' onClick={() => navigate('/view_pr')}>
                        Go Back
                    </button>
                </div>
            </div>
        )
    }

    const lineItems = Array.isArray(pr.lineItems) ? pr.lineItems : []
    const isPending = approvalStatus === 'pending'
    const isApproved = approvalStatus === 'approved'
    const isRejected = approvalStatus === 'rejected'
    const isReadOnly = isApproved || isRejected
    const showActions = canApprove(userRole) && isPending

    return (
        <div>
            {showReject && (
                <RejectModal
                    onConfirm={handleReject}
                    onCancel={() => { setShowReject(false); setActionError('') }}
                    loading={actionLoading}
                />
            )}

            <PageHeader
                pageName='PR DETAILS'
                breadcrump1='View PR'
                breadcrump2={pr.prId || prid?.slice(-8).toUpperCase()}
                link='/view_pr'
            />

            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>

                {/* Back */}
                <button
                    onClick={() => navigate('/view_pr')}
                    className='btn btn-light border mb-4 text-sm font-semibold flex items-center gap-2'
                >
                    <FontAwesomeIcon icon={faChevronLeft} /> Back to Purchase Requests
                </button>

                {/* ── Feedback banners ── */}
                {actionSuccess && (
                    <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-green-50 border border-green-200 text-green-700 text-sm'>
                        <FontAwesomeIcon icon={faCheckCircle} /> {actionSuccess}
                    </div>
                )}
                {actionError && (
                    <div className='flex items-center gap-2 rounded-lg px-4 py-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm'>
                        <FontAwesomeIcon icon={faExclamationTriangle} /> {actionError}
                    </div>
                )}

                {/* ── Main card ── */}
                <div className='bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-4'>

                    {/* Card header */}
                    <div className='flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100 bg-gray-50'>
                        <div className='flex items-center gap-2'>
                            <FontAwesomeIcon icon={faFileContract} className='text-purple-600' />
                            <h5 className='font-bold text-gray-800 mb-0'>
                                Purchase Request Details
                            </h5>
                        </div>
                        <StatusBadge status={approvalStatus} />
                    </div>

                    {/* ── Header fields (read-only always) ── */}
                    <div className='px-5 py-4 border-b border-gray-100'>
                        <Row>
                            <InfoField label='PR ID' value={pr.prId} />
                            <InfoField label='Request Date' value={formatDate(pr.date || pr.createdAt)} />
                            <InfoField label='Request Raised By' value={pr.requestRaisedBy || pr.createdBy?.name} />
                            <InfoField
                                label='PR Type'
                                value={<OemBadge isOem={!!pr.isOem} />}
                            />
                        </Row>
                        <Row>
                            <InfoField
                                label='Approval Status'
                                value={<StatusBadge status={approvalStatus} />}
                            />
                        </Row>

                        {pr.salesOrder && (
                            <>
                                <Row>
                                    <InfoField
                                        label='Linked Sales Order'
                                        colMd={6}
                                        value={
                                            pr.salesOrder?.salesOrderNumber ||
                                            (typeof pr.salesOrder === 'string' ? pr.salesOrder : '—')
                                        }
                                    />

                                    <InfoField
                                        label='Client'
                                        colMd={6}
                                        value={
                                            pr.salesOrder?.client?.companyName ||
                                            (typeof pr.salesOrder === 'string' ? pr.salesOrder : '—')
                                        }
                                    />
                                </Row>
                            </>
                        )}

                    </div>

                    {/* ── Rejection banner ── */}
                    {isRejected && pr.rejectionReason && (
                        <div className='px-5 py-3 border-b border-gray-100'>
                            <div className='bg-red-50 border border-red-200 rounded-lg px-4 py-3'>
                                <div className='text-xs text-red-700 font-bold mb-1'>
                                    <FontAwesomeIcon icon={faTimesCircle} className='mr-1' />
                                    Rejection Reason
                                </div>
                                <p className='text-sm text-red-800 mb-0'>{pr.rejectionReason}</p>
                                {pr.rejectedBy && (
                                    <div className='mt-1.5 text-xs text-gray-400'>
                                        Rejected by{' '}
                                        <strong>{pr.rejectedBy?.name || pr.rejectedBy}</strong>
                                        {pr.rejectedAt ? ` on ${formatDate(pr.rejectedAt)}` : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* ── Approval banner ── */}
                    {isApproved && pr.approvedBy && (
                        <div className='px-5 py-3 border-b border-gray-100'>
                            <div className='bg-green-50 border border-green-200 rounded-lg px-4 py-3'>
                                <div className='text-xs text-green-700 font-bold mb-1'>
                                    <FontAwesomeIcon icon={faCheckCircle} className='mr-1' />
                                    Approved
                                </div>
                                <p className='text-sm text-green-800 mb-0'>
                                    Approved by{' '}
                                    <strong>{pr.approvedBy?.name || pr.approvedBy}</strong>
                                    {pr.approvedAt ? ` on ${formatDate(pr.approvedAt)}` : ''}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* ── Line Items ── */}
                    <div className='px-5 py-4'>
                        <div className='text-xs text-gray-400 font-bold uppercase tracking-wide mb-3'>
                            Line Item
                        </div>
                        <div className='border border-gray-200 rounded-lg overflow-hidden'>
                            <div className='table-responsive'>
                                <table className='table align-middle table-nowrap mb-0 so-table'>
                                    <thead className='table-light'>
                                        <tr>
                                            <th>SI</th>
                                            <th>Model</th>
                                            <th>Part Code</th>
                                            <th className='text-center'>Qty</th>
                                            <th>Specification</th>
                                            <th>Required Delivery</th>
                                            <th>Last Delivery Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItems.length > 0 ? (
                                            lineItems.map((li, idx) => {
                                                if (isEditing) {
                                                    const editItem = editLineItems[idx]
                                                    if (!editItem) return null
                                                    const maxQtyForSelect = editItem.maxQty
                                                    const qtyOptions = Array.from({ length: maxQtyForSelect + 1 }, (_, i) => i)

                                                    return (
                                                        <tr key={li._id || idx}>
                                                            <td>{idx + 1}</td>
                                                            <td className='font-semibold'>
                                                                {li.model?.modelName || li.model?.name || (typeof li.model === 'string' ? li.model : '—')}
                                                            </td>
                                                            <td className='font-semibold'>
                                                                {li.model?.partCode || '—'}
                                                            </td>
                                                            <td className='text-center'>
                                                                <select
                                                                    className='form-select form-select-sm block w-20 mx-auto'
                                                                    value={editItem.editQty}
                                                                    onChange={(e) => handleQtyChange(idx, e.target.value, editItem.maxQty)}
                                                                >
                                                                    <option value={0}>0</option>
                                                                    {qtyOptions.filter(n => n > 0).map(n => <option key={n} value={n}>{n}</option>)}
                                                                </select>
                                                                <div className='text-xs text-gray-500 mt-1 max-w-[100px]'>Max: {editItem.maxQty}</div>
                                                            </td>
                                                            <td>
                                                                <input
                                                                    type='text'
                                                                    className='form-control form-control-sm'
                                                                    value={editItem.editSpec || ''}
                                                                    onChange={e => handleSpecChange(idx, e.target.value)}
                                                                />
                                                            </td>
                                                            <td>{formatDate(li.requiredDeliveryDate)}</td>
                                                            <td>{formatDate(li.lastDateOfOrderDelivery)}</td>
                                                        </tr>
                                                    )
                                                }

                                                return (
                                                    <tr key={li._id || idx}>
                                                        <td>{idx + 1}</td>
                                                        <td className='font-semibold'>
                                                            {li.model?.modelName ||
                                                                li.model?.name ||
                                                                (typeof li.model === 'string'
                                                                    ? li.model
                                                                    : '—')}
                                                        </td>
                                                        <td className='font-semibold'>
                                                            {li.model?.partCode || '—'}
                                                        </td>
                                                        <td className='text-center font-bold text-purple-700'>
                                                            {li.quantity}
                                                        </td>
                                                        <td className='text-sm text-gray-600 max-w-xs break-words whitespace-normal'>
                                                            {li.specification || '—'}
                                                        </td>
                                                        <td>{formatDate(li.requiredDeliveryDate)}</td>
                                                        <td>{formatDate(li.lastDateOfOrderDelivery)}</td>
                                                    </tr>
                                                )
                                            })
                                        ) : (
                                            <tr>
                                                <td
                                                    colSpan={6}
                                                    className='text-center py-6 text-gray-400 text-sm'
                                                >
                                                    No line items
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {/* ── Approval actions ── */}
                    {showActions && (
                        <div className='px-5 py-4 border-t border-gray-100 bg-gray-50'>
                            <p className='text-sm font-semibold text-gray-700 mb-3'>
                                Approval Decision
                            </p>
                            <div className='flex justify-end gap-3'>
                                <button
                                    id='btn-reject-pr'
                                    className='btn btn-danger px-4 font-bold flex items-center gap-2'
                                    onClick={() => setShowReject(true)}
                                    disabled={actionLoading}
                                >
                                    <FontAwesomeIcon icon={faTimesCircle} /> Reject
                                </button>
                                <button
                                    id='btn-approve-pr'
                                    className='btn btn-success px-4 font-bold flex items-center gap-2'
                                    onClick={handleApprove}
                                    disabled={actionLoading}
                                >
                                    {actionLoading ? (
                                        <>
                                            <FontAwesomeIcon icon={faSpinner} spin /> Processing…
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faCheckCircle} /> Approve
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── Edit/Resubmit Actions ── */}
                    {!canApprove(userRole) && isRejected && !isEditing && (
                        <div className='px-5 py-4 border-t border-gray-100 bg-gray-50 flex justify-end'>
                            <button
                                className='btn btn-primary px-4 font-bold flex items-center gap-2'
                                onClick={handleEditClick}
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Fetching Details…</>
                                ) : (
                                    <><FontAwesomeIcon icon={faEdit} /> Edit & Resubmit</>
                                )}
                            </button>
                        </div>
                    )}

                    {isEditing && (
                        <div className='px-5 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3'>
                            <button
                                className='btn btn-light border font-bold'
                                onClick={() => setIsEditing(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </button>
                            <button
                                className='btn btn-success px-4 font-bold flex items-center gap-2'
                                onClick={handleResubmit}
                                disabled={actionLoading}
                            >
                                {actionLoading ? (
                                    <><FontAwesomeIcon icon={faSpinner} spin /> Saving…</>
                                ) : (
                                    <><FontAwesomeIcon icon={faSave} /> Save & Resubmit</>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Read-only notice */}
                    {isReadOnly && !isEditing && (
                        <div className='px-5 py-3 border-t border-gray-100 bg-gray-50 text-xs text-gray-400 text-center'>
                            This purchase request is{' '}
                            <strong className='capitalize'>{approvalStatus}</strong> and cannot be
                            modified.
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default PRDetail
